import { usePathname } from 'next/navigation';
import { cn } from '@savvy-kit/lib/utils';
import Banner from '@savvy-kit/patterns/banner';
import { PageHeaderSlotProvider } from '@savvy-kit/templates/page-header-slot';
import { SidebarProvider } from '@savvy-kit/templates/sidebar';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ErrorBoundary } from 'react-error-boundary';
import { withAdvisorGuard } from 'src/hocs/with-advisor-guard';
import useSalesforceReAuth from 'src/hooks/use-salesforce-reauth';

import { HouseholdProvider } from '@savvy/deprecated-shared/contexts/household-context';
import { loggedInAsAdminID } from '@savvy/deprecated-shared/utils/savvy-auth/login-as-advisor';

import AdminImpersonationBarV2 from '../admin-impersonation-bar-v2';
import SideNav from '../nav/advisor-side-nav/advisor-side-nav';
import { ReportIssueModal } from '../report-issue/report-issue-modal';
import { useReportIssueModal } from '../report-issue/use-report-issue-modal';
import { useAdvisorDashNav } from './advisor-dash-nav-context';
import NotificationsBellButton from './notifications-bell-button';

type Props = {
  readonly children?: React.ReactNode;
  readonly forceNotificationsBell?: boolean;
};

function AdvisorDashContent({ children, forceNotificationsBell = false }: Props) {
  const { open, setOpen } = useAdvisorDashNav();
  const reportIssueOpen = useReportIssueModal((s) => s.isOpen);
  const closeReportIssueModal = useReportIssueModal((s) => s.closeReportIssueModal);
  const openReportIssueModal = useReportIssueModal((s) => s.openReportIssueModal);
  const flags = useFlags();
  const servalSupport = Boolean(flags['flagServalSupport']);
  const notificationsV2 = Boolean(flags['flagNotificationsV2']) || forceNotificationsBell;
  const pathname = usePathname();

  useSalesforceReAuth();

  const isAdminLoggedInAsAdvisor = loggedInAsAdminID() !== null;

  return (
    <>
      {isAdminLoggedInAsAdvisor && (
        <div className="print:hidden">
          <AdminImpersonationBarV2 />
        </div>
      )}
      <SidebarProvider open={open} onOpenChange={setOpen} topOffset={isAdminLoggedInAsAdvisor ? 52 : 0}>
        <div className="print:hidden">
          <SideNav />
        </div>
        <div className="flex flex-1 flex-col min-w-0 bg-background SavvyPageContainer">
          <div className={cn('relative flex-1', isAdminLoggedInAsAdvisor && `[&_.SavvyPageTitleContainer]:pt-[54px]`)}>
            {/* Scope render errors to the page content so one throwing page
                (e.g. a bad table row) no longer takes the whole app to the
                generic Next.js _error screen. Mirrors OfferingsLayout /
                ClientHouseholdLayout.

                resetKeys={[pathname]}: this layout is a persistent getLayout
                shell mounted across every advisor-dash page, so the boundary
                does NOT unmount on navigation. Without a reset tied to the
                route, a caught error would strand the fallback banner on every
                subsequent page until a hard refresh. Keying on pathname clears
                the error when the advisor navigates away (and, unlike `key`,
                leaves the page subtree mounted on benign query-only changes). */}
            <ErrorBoundary
              resetKeys={[pathname]}
              fallback={
                <div className="m-9">
                  <Banner
                    variant="destructive"
                    title="Something went wrong loading this page"
                    description={
                      servalSupport ? (
                        <>
                          Looks like we&apos;re having an issue loading this page. Please{' '}
                          <button type="button" onClick={() => openReportIssueModal()} className="underline">
                            create a support request
                          </button>{' '}
                          for assistance.
                        </>
                      ) : (
                        "Looks like we're having an issue loading this page. Please reach out to @product-goalie in Slack for support."
                      )
                    }
                  />
                </div>
              }
              onError={(error, info) => {
                console.error(error, info);
              }}
            >
              <PageHeaderSlotProvider slot={notificationsV2 ? <NotificationsBellButton /> : null}>
                {children}
              </PageHeaderSlotProvider>
            </ErrorBoundary>
          </div>
        </div>
      </SidebarProvider>
      <ReportIssueModal
        open={reportIssueOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeReportIssueModal();
        }}
      />
    </>
  );
}

function AdvisorDashTopNavLayout({ children, forceNotificationsBell = false }: Props) {
  return (
    <HouseholdProvider>
      <AdvisorDashContent forceNotificationsBell={forceNotificationsBell}>{children}</AdvisorDashContent>
    </HouseholdProvider>
  );
}

export default withAdvisorGuard(AdvisorDashTopNavLayout);
